/**
 * FlowHub - Application Router
 * Handles routing and role-based access control
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store/authStore';
import { Suspense } from 'react';
import { Spin } from 'antd';

// Layouts
import { AdminLayout } from '../shared/components/AdminLayout';
import { ClientLayout } from '../shared/components/ClientLayout';

// Auth Pages
import LoginPage from '../features/auth/pages/LoginPage';
import RegisterPage from '../features/auth/pages/RegisterPage';
import ResetPasswordPage from '../features/auth/pages/ResetPasswordPage';

// Admin Pages
import { AdminDashboardPage } from '../features/admin/pages/AdminDashboardPage';
import { N8nInstancesPage } from '../features/admin/pages/N8nInstancesPage';
import { ProductsPage } from '../features/admin/pages/ProductsPage';
import { UsersPage } from '../features/admin/pages/UsersPage';
import { ClientProductsPage } from '../features/admin/pages/ClientProductsPage';
import { ClientProductDetailPage } from '../features/admin/pages/ClientProductDetailPage';
import { AdminSupportPage } from '../features/admin/pages/AdminSupportPage';
import { AdminPaymentsPage } from '../features/admin/pages/AdminPaymentsPage';
import FormApprovalsPage from '../features/admin/pages/FormApprovalsPage';
import FormsPage from '../features/admin/pages/FormsPage';
import FormSubmissionsPage from '../features/admin/pages/FormSubmissionsPage';

// Client Pages
import { MarketplacePage } from '../features/client/pages/MarketplacePage';
import MyProductsPage from '../features/client/pages/MyProductsPage';
import MySubscriptionsPage from '../features/client/pages/MySubscriptionsPage';
import ClientDashboardPage from '../features/client/pages/ClientDashboardPage';
import ClientSupportPage from '../features/client/pages/ClientSupportPage';
import ClientProfilePage from '../features/client/pages/ClientProfilePage';
import ClientSettingsPage from '../features/client/pages/ClientSettingsPage';
import { AdminProfilePage } from '@/features/admin/pages/AdminProfilePage';
import { CheckoutPage } from '../features/client/pages/CheckoutPage';
import { PaymentHistoryPage } from '../features/client/pages/PaymentHistoryPage';
import { ClientFormPage } from '../features/client/pages/ClientFormPage';

/**
 * Loading fallback component
 */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spin size="large" spinning>
      <div style={{ minHeight: 200 }} />
    </Spin>
  </div>
);

/**
 * Protected route wrapper - only allows authenticated users
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('admin' | 'client')[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.profile.role)) {
    // Redirect to appropriate dashboard based on role
    return <Navigate to={user.profile.role === 'admin' ? '/admin' : '/client'} replace />;
  }

  return <>{children}</>;
};

/**
 * Public route wrapper - redirects authenticated users to their dashboard
 */
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated && user) {
    const redirectTo = user.profile.role === 'admin' ? '/admin' : '/client';
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

/**
 * Main application router
 */
function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="workflows" element={<ProductsPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="clients" element={<UsersPage />} />
          <Route path="clients/:clientId/products" element={<ClientProductsPage />} />
          <Route path="clients/:clientId/products/:productId" element={<ClientProductDetailPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="n8n-instances" element={<N8nInstancesPage />} />
          <Route path="forms" element={<FormsPage />} />
          <Route path="form-submissions/:formId" element={<FormSubmissionsPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="form-approvals" element={<FormApprovalsPage />} />
          <Route path="support" element={<AdminSupportPage />} />
          <Route path="profile" element={<AdminProfilePage />} />
          {/* Client Routes
          <Route path="settings" element={<EnvironmentVariablesPage />} />
           */}
        </Route>

        {/* Client Routes */}
        <Route
          path="/client/*"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/client/dashboard" replace />} />
          <Route path="dashboard" element={<ClientDashboardPage />} />
          <Route path="marketplace" element={<MarketplacePage />} />
          <Route path="workflows" element={<MyProductsPage />} />
          <Route path="subscriptions" element={<MySubscriptionsPage />} />
          <Route path="my-products" element={<Navigate to="/client/workflows" replace />} />
          <Route path="form/:clientProductId" element={<ClientFormPage />} />
          <Route path="checkout/:productId" element={<CheckoutPage />} />
          <Route path="payments" element={<PaymentHistoryPage />} />
          <Route path="support" element={<ClientSupportPage />} />
          <Route path="profile" element={<ClientProfilePage />} />
          <Route path="settings" element={<ClientSettingsPage />} />
        </Route>

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 404 - Not Found */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center min-h-screen">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
              <p className="text-gray-600 mb-8">Page not found</p>
              <a href="/" className="text-blue-600 hover:underline">
                Go back home
              </a>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default AppRouter;
